import { useEffect, useState } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
  type: 'circle' | 'hexagon' | 'diamond';
  color: string;
}

export const HeroParticles = () => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if mobile
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);
    
    // Skip particles on mobile for better performance
    if (mobile) {
      return;
    }
    
    // Generate particles only on desktop
    const particleCount = 12;
    
    const colors = [
      'hsl(var(--primary) / 0.4)',
      'hsl(var(--secondary) / 0.3)',
      'hsl(45 100% 60% / 0.3)', // gold
      'hsl(var(--accent) / 0.3)',
    ];
    
    const types: ('circle' | 'hexagon' | 'diamond')[] = ['circle', 'hexagon', 'diamond'];
    
    const newParticles: Particle[] = Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 8 + 4,
      duration: Math.random() * 15 + 10,
      delay: Math.random() * 5,
      type: types[Math.floor(Math.random() * types.length)],
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
    
    setParticles(newParticles);
  }, []);

  // Don't render particles on mobile
  if (isMobile) {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Static dots only on mobile */}
        <div className="absolute top-[10%] right-[15%] w-2 h-2 bg-primary/30 rounded-full" />
        <div className="absolute top-[30%] left-[10%] w-1.5 h-1.5 bg-gold/40 rounded-full" />
        <div className="absolute bottom-[25%] right-[20%] w-2 h-2 bg-secondary/30 rounded-full" />
      </div>
    );
  }

  const renderShape = (particle: Particle) => {
    const baseStyle = {
      width: particle.size,
      height: particle.size,
      backgroundColor: particle.color,
    };

    switch (particle.type) {
      case 'hexagon':
        return (
          <div
            style={{
              ...baseStyle,
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
            }}
          />
        );
      case 'diamond':
        return (
          <div
            style={{
              ...baseStyle,
              transform: 'rotate(45deg)',
            }}
          />
        );
      default:
        return (
          <div
            style={{
              ...baseStyle,
              borderRadius: '50%',
            }}
          />
        );
    }
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none hero-particles">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-float-particle"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            animationDuration: `${particle.duration}s`,
            animationDelay: `${particle.delay}s`,
            opacity: 0.6,
            willChange: 'transform',
            transform: 'translateZ(0)',
          }}
        >
          {renderShape(particle)}
        </div>
      ))}
      
      {/* Floating geometric shapes - desktop only */}
      {/* Large floating ring */}
      <div 
        className="absolute top-[15%] right-[10%] w-32 h-32 border-2 border-primary/20 rounded-full animate-spin-slow"
        style={{ animationDuration: '30s' }}
      />
      
      {/* Floating hexagon outline */}
      <div 
        className="absolute bottom-[20%] left-[8%] w-20 h-20 border border-secondary/20 animate-float"
        style={{ 
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          animationDelay: '2s',
          animationDuration: '8s',
        }}
      />
      
      {/* Glowing orb */}
      <div 
        className="absolute top-[60%] right-[15%] w-4 h-4 bg-gold/60 rounded-full blur-sm animate-pulse"
        style={{ boxShadow: '0 0 20px hsl(45 100% 60% / 0.5)' }}
      />
      
      {/* Diamond shape */}
      <div 
        className="absolute top-[30%] left-[20%] w-6 h-6 bg-accent/20 rotate-45 animate-float"
        style={{ animationDelay: '1.5s', animationDuration: '6s' }}
      />
    </div>
  );
};
